// Slim, persistent banner that appears under the topbar on every authenticated
// page when the tenant's subscription is in a retention-relevant state.
// Three retention states (see lib/subscription-state.ts): scheduled-cancel,
// canceled, past-due. Healthy / 'none' renders nothing.
//
// Click target deliberately routes to /billing for full context (the rich
// surface with portal handoff, invoices, plan-switching). Inline mutation
// would invite half-state bugs in a slim banner that lacks the space to
// confirm + show errors. The Home retention card and the playground panel
// own the "one-click resume" experience — those have room to surface
// pending / error states clearly.
//
// State A (scheduled-cancel) is dismissible per session (sessionStorage
// keyed by cancelAt timestamp — re-shows if the cancel date changes via
// Stripe Portal in a different tab). States B and C are NOT dismissible:
// the service is gated, hiding the banner helps no one.

import { Link, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, CreditCard, ShieldAlert, X } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useApiClient } from '../../lib/providers.js';
import { usageApi, USAGE_QUERY_KEY } from '../../lib/api/usage.js';
import { deriveSubscriptionState } from '../../lib/subscription-state.js';
import { formatDate } from '../../lib/format-date.js';
import { Button } from '../ui/button.js';
import { cn } from '../../lib/utils.js';

const DISMISS_KEY_PREFIX = 'cortex.banner.scheduledCancelDismissed.';

export function SubscriptionStatusBanner() {
  const api = useApiClient();
  const { pathname } = useLocation();
  const summary = useQuery({
    queryKey: USAGE_QUERY_KEY,
    queryFn: () => usageApi.summary(api),
    // Reuses any cache populated by Home / Usage / Billing pages — they
    // already prime this query, so the banner usually has data on first
    // paint.
  });

  const plan = summary.data?.plan;
  const state = plan ? deriveSubscriptionState(plan) : 'none';

  // Dismissal is keyed by the cancel date so a Stripe Portal change to the
  // schedule re-surfaces the banner. SessionStorage scope = until tab close,
  // which feels right for a "got it, not now" affordance without nagging.
  const dismissKey =
    state === 'scheduled-cancel' && plan?.cancelAt ? `${DISMISS_KEY_PREFIX}${plan.cancelAt}` : null;
  const [dismissed, setDismissed] = useState(false);
  useEffect(() => {
    if (!dismissKey) {
      setDismissed(false);
      return;
    }
    setDismissed(sessionStorage.getItem(dismissKey) === '1');
  }, [dismissKey]);

  if (state === 'none') return null;
  if (state === 'scheduled-cancel' && dismissed) return null;
  // Don't render the banner on /billing — the page already shows richer
  // in-context detail with the same Resume CTA. A slim banner above the
  // dedicated card would just be a duplicate row.
  if (pathname === '/billing') return null;

  const cancelDate = plan?.cancelAt ? formatDate(plan.cancelAt) : null;

  let content: BannerContent;
  if (state === 'scheduled-cancel') {
    content = {
      tone: 'warning',
      icon: AlertTriangle,
      message: cancelDate
        ? `Subscription ends on ${cancelDate} — falls to Free tier after, data preserved.`
        : 'Subscription is scheduled to end — falls to Free tier after, data preserved.',
      cta: 'Continue subscription',
    };
  } else if (state === 'canceled') {
    content = {
      tone: 'destructive',
      icon: ShieldAlert,
      message: 'Service paused. Reactivate to restore access — everything is preserved.',
      cta: 'Reactivate',
    };
  } else {
    content = {
      tone: 'destructive',
      icon: CreditCard,
      message: 'Payment failed. Update your card to keep your service running.',
      cta: 'Update payment method',
    };
  }

  const onDismiss = () => {
    if (dismissKey) sessionStorage.setItem(dismissKey, '1');
    setDismissed(true);
  };

  return (
    <div
      role="alert"
      className={cn(
        'flex items-center gap-3 border-b px-4 py-2 text-sm md:px-6',
        content.tone === 'warning'
          ? 'border-warning/30 bg-warning/10 text-warning-foreground'
          : 'border-destructive/30 bg-destructive/10 text-destructive',
      )}
    >
      <content.icon className="h-4 w-4 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1 truncate">{content.message}</p>
      <Button asChild size="sm" variant={content.tone === 'warning' ? 'default' : 'destructive'}>
        <Link to="/billing">{content.cta}</Link>
      </Button>
      {state === 'scheduled-cancel' && (
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onDismiss}
          aria-label="Dismiss subscription banner for this session"
        >
          <X aria-hidden />
        </Button>
      )}
    </div>
  );
}

interface BannerContent {
  tone: 'warning' | 'destructive';
  icon: typeof AlertTriangle;
  message: string;
  cta: string;
}
